import type { ReactNode } from 'react';
import { S, t, type Locale } from '../../i18n/strings';
import { AwardBadge } from './AwardBadge';
import { ACHIEVEMENT_INFO, ACHIEVEMENTS, type AchievementId } from './achievements';
import styles from './pixel.module.css';

/** Combien de temps une notification reste à l'écran, en millisecondes. */
export const TOAST_MS = 5000;
/** Au-delà, les plus anciennes disparaissent : la pile ne mange pas l'écran. */
export const TOAST_MAX = 3;

export interface Toast {
  /** Clé de rendu : un succès ne tombe qu'une fois, mais la clé reste unique. */
  readonly key: number;
  readonly id: AchievementId;
}

interface ToastProps {
  readonly toasts: readonly Toast[];
  readonly lang: Locale;
}

/**
 * Les notifications de succès, en bas à droite de l'écran de jeu.
 *
 * Même mise en page que celles de Steam : vignette à gauche, mention en petit,
 * nom en gros. Elles s'empilent vers le haut et se retirent toutes seules — la
 * minuterie est tenue par le composant du jeu, pas ici, pour que ce fichier
 * reste un affichage pur.
 *
 * `aria-live="polite"` et non `assertive` : un succès n'interrompt pas ce qu'un
 * lecteur d'écran est en train d'annoncer, il attend son tour.
 */
export function AwardToasts({ toasts, lang }: ToastProps): ReactNode {
  return (
    <div className={styles.toastStack} aria-live="polite">
      {toasts.map((toast) => {
        const award = ACHIEVEMENT_INFO[toast.id];
        return (
          <div key={toast.key} className={styles.toast}>
            <AwardBadge badge={award.badge} className={styles.toastBadge} />
            <div className={styles.toastText}>
              <p className={styles.toastHead}>{t(S.pixel.awards.unlocked, lang)}</p>
              <p className={styles.toastName}>{t(award.name, lang)}</p>
              <p className={styles.toastLine}>{t(award.line, lang)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface PanelProps {
  readonly won: readonly AchievementId[];
  readonly lang: Locale;
  readonly onClose: () => void;
}

/**
 * Le tableau des succès, par-dessus l'écran de jeu.
 *
 * Un succès qui n'est pas tombé garde son nom mais cache sa ligne derrière
 * `? ? ?`, comme les cases vides de l'inventaire du skin : de quoi savoir qu'il
 * existe, pas de quoi savoir comment l'obtenir.
 */
export function AwardsPanel({ won, lang, onClose }: PanelProps): ReactNode {
  return (
    /*
     * Une `section` et non un second `dialog` : le tableau vit déjà à
     * l'intérieur de la fenêtre modale du jeu, et deux `aria-modal` imbriqués
     * ne veulent rien dire pour un lecteur d'écran.
     */
    <section className={styles.awardsPanel} aria-label={t(S.pixel.awards.panel, lang)}>
      <p className={styles.awardsTitle}>{t(S.pixel.awards.panel, lang)}</p>
      <ul className={styles.awardsList}>
        {ACHIEVEMENTS.map((id) => {
          const award = ACHIEVEMENT_INFO[id];
          const unlocked = won.includes(id);
          return (
            <li key={id} className={unlocked ? styles.awardRow : `${styles.awardRow} ${styles.awardRowLocked}`}>
              <AwardBadge badge={award.badge} className={styles.awardRowBadge} locked={!unlocked} />
              <span className={styles.awardRowName}>{t(award.name, lang)}</span>
              <span className={styles.awardRowLine}>
                {unlocked ? t(award.line, lang) : t(S.pixel.awards.locked, lang)}
              </span>
            </li>
          );
        })}
      </ul>
      <button type="button" className={styles.gameButton} onClick={onClose}>
        {t(S.pixel.awards.close, lang)}
      </button>
    </section>
  );
}
